import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'

const CallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`

const StatusMessage = styled.div<{ $type: 'loading' | 'success' | 'error' }>`
  font-size: ${({ theme }) => theme.typography.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme, $type }) => {
    switch ($type) {
      case 'success':
        return theme.colors.primary
      case 'error':
        return theme.colors.error
      default:
        return theme.colors.text
    }
  }};
`

const Spinner = styled.div`
  border: 3px solid ${({ theme }) => theme.colors.border};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto ${({ theme }) => theme.spacing.md};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`
/**
 * Close window after 3 seconds
 */
const closeWindow = () => {
  setTimeout(() => window.close(), 3000)
}

export const GarminCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('Processing Garmin authorization...')

  useEffect(() => {
    const exchangeTokensWithBackend = async (code: string, state: string, codeVerifier: string) => {
      const { error } = await supabase.functions.invoke('garmin-oauth', {
        body: {
          oauth_code: code,
          oauth_state: state,
          oauth_code_verifier: codeVerifier,
        },
      })
      return error
    }

    const backfillData = async () => {
      try {
        // Check if backfill is needed
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!user) {
          console.error('Could not find auth user')
          return
        }

        // Check for workouts in the last month
        // TODO: extract to repository/service
        const oneMonthAgo = new Date()
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
        const { data: recentWorkouts, error: workoutsError } = await supabase
          .from('workouts')
          .select('id')
          .eq('user_id', user.id)
          .gte('scheduled_date', oneMonthAgo.toISOString())
          .limit(1)
        if (workoutsError) {
          console.error('Failed to fetch workouts:', workoutsError)
          return
        }

        // If no workouts found, trigger backfill
        if (!recentWorkouts || recentWorkouts.length === 0) {
          console.log('No recent workouts found, triggering backfill...')
          const endDate = new Date().toISOString()
          const startDate = oneMonthAgo.toISOString()
          const { error: backfillError } = await supabase.functions.invoke('data-sync', {
            body: {
              startDate,
              endDate,
            },
          })
          if (backfillError) {
            console.error('Backfill error:', backfillError)
          } else {
            console.log('Backfill request successful')
          }
        } else {
          console.log('Recent workouts found, skipping backfill')
        }
      } catch (backfillError) {
        return backfillError
      }
    }

    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        if (!code) {
          setStatus('error')
          setMessage('No code found in the response.')
          return
        }

        // Verify the state in storage garmin_auth_state matches the state in redirect url?
        const state = urlParams.get('state')
        const storedState = localStorage.getItem('garmin_auth_state')
        if (!state) {
          setStatus('error')
          setMessage('No state found in the response.')
          return
        }
        if (!storedState) {
          setStatus('error')
          setMessage('No state found in local storage.')
          return
        }
        if (state !== storedState) {
          setStatus('error')
          setMessage('State mismatch. Please try again.')
          return
        }

        // code_verifier was stored by the invoking browser tab/window
        const codeVerifier = localStorage.getItem('garmin_auth_code_verifier')
        if (!codeVerifier) {
          setStatus('error')
          setMessage('Could not find code verifier in local storage.')
          return
        }

        const error = await exchangeTokensWithBackend(code, state, codeVerifier)
        if (error) {
          console.error('Garmin OAuth error:', error)
          setStatus('error')
          setMessage('Failed to complete Garmin authorization.')
          return
        }
        // Signal success to parent window
        localStorage.setItem('garmin_auth_success', 'true')
        setStatus('success')
        setMessage('Successfully connected to Garmin!')

        const backfillError = await backfillData()
        if (backfillError) {
          console.error('Error during backfill:', backfillError)
        }
      } catch (error) {
        console.error('Callback error:', error)
        setStatus('error')
        setMessage('An unexpected error occurred.')
      }

      closeWindow()
    }

    handleCallback()
  }, [])

  return (
    <CallbackContainer>
      {status === 'loading' && <Spinner />}
      <StatusMessage $type={status}>{message}</StatusMessage>
      {status === 'success' && <p>This window will close automatically...</p>}
      {status === 'error' && <p>This window will close automatically...</p>}
    </CallbackContainer>
  )
}
